import { useState, useMemo } from 'react';
import { X, Plus, Clock, CheckCircle2 } from 'lucide-react';
import BlockCapacityBar from './BlockCapacityBar';
import TimeBlockTaskItem from './TimeBlockTaskItem';
import { calculateBlockCapacity } from '../../utils/timeBlockCapacity';
import { formatEventTime } from '../../hooks/useGoogleCalendar';

export default function TimeBlockTaskPanel({
  block,
  tasks,
  allBlocks = [],
  onClose,
  onToggleComplete,
  onEditTask,
  onRescheduleTask,
  onDelegateTask,
  onAddTask,
  isMobile = false
}) {
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Calculate capacity
  const capacity = useMemo(() => {
    if (!block) return null;
    return calculateBlockCapacity(block, tasks);
  }, [block, tasks]);

  // Get available blocks for rescheduling (excluding current block)
  const availableBlocks = useMemo(() => {
    return allBlocks
      .filter(b => b.id !== block?.id)
      .map(b => ({
        id: b.id,
        summary: b.summary,
        timeRange: formatEventTime(b),
        date: b.start?.dateTime?.split('T')[0] || b.start?.date
      }));
  }, [allBlocks, block]);

  // Split tasks into done and not done
  const { activeTasks, completedTasks } = useMemo(() => {
    const active = tasks.filter(t => t.status !== 'done');
    const completed = tasks.filter(t => t.status === 'done');
    return { activeTasks: active, completedTasks: completed };
  }, [tasks]);

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    if (!quickAddTitle.trim() || !onAddTask) return;

    setIsAdding(true);
    try {
      await onAddTask(quickAddTitle.trim(), block);
      setQuickAddTitle('');
    } finally {
      setIsAdding(false);
    }
  };

  if (!block) {
    return (
      <div className={`
        flex flex-col items-center justify-center h-full
        ${isMobile ? 'p-6' : 'p-8'}
        text-center
      `}>
        <Clock size={48} className="text-text-muted mb-4 opacity-50" />
        <h3 className="text-lg font-medium text-text-secondary mb-2">
          No Block Selected
        </h3>
        <p className="text-sm text-text-tertiary max-w-xs">
          Click on a time block in your schedule to see and manage its tasks
        </p>
      </div>
    );
  }

  return (
    <div className={`
      flex flex-col h-full bg-surface-base
      ${isMobile ? 'rounded-t-2xl' : 'border-l border-border'}
    `}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-text-primary truncate">
              {block.summary}
            </h3>
            <p className="text-sm text-text-tertiary mt-0.5">
              {formatEventTime(block)}
            </p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-md bg-transparent border-none text-text-muted cursor-pointer hover:bg-surface-hover hover:text-text-secondary transition-all ml-2"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Capacity bar */}
        {capacity && (
          <BlockCapacityBar
            fillPercentage={capacity.fillPercentage}
            remainingMinutes={capacity.remainingMinutes}
            isOverbooked={capacity.isOverbooked}
          />
        )}
      </div>

      {/* Task list */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {/* Active tasks */}
        {activeTasks.length > 0 ? (
          activeTasks.map((task) => (
            <TimeBlockTaskItem
              key={task.id}
              task={task}
              onToggleComplete={onToggleComplete}
              onEdit={onEditTask}
              onReschedule={onRescheduleTask}
              onDelegate={onDelegateTask}
              availableBlocks={availableBlocks}
            />
          ))
        ) : (
          <div className="text-center py-8">
            <CheckCircle2 size={32} className="text-success mx-auto mb-2 opacity-50" />
            <p className="text-sm text-text-tertiary">
              No tasks in this block
            </p>
          </div>
        )}

        {/* Completed tasks */}
        {completedTasks.length > 0 && (
          <div className="pt-4">
            <p className="text-[10px] text-text-muted uppercase tracking-wider mb-2">
              Completed ({completedTasks.length})
            </p>
            {completedTasks.map((task) => (
              <TimeBlockTaskItem
                key={task.id}
                task={task}
                onToggleComplete={onToggleComplete}
                onEdit={onEditTask}
                onReschedule={onRescheduleTask}
                availableBlocks={availableBlocks}
              />
            ))}
          </div>
        )}
      </div>

      {/* Quick add */}
      {onAddTask && (
        <div className="p-4 border-t border-border">
          <form onSubmit={handleQuickAdd} className="flex gap-2">
            <input
              type="text"
              placeholder="Add task to this block..."
              value={quickAddTitle}
              onChange={(e) => setQuickAddTitle(e.target.value)}
              disabled={isAdding}
              className="flex-1 py-2 px-3 bg-surface-elevated border border-border rounded-lg text-sm text-text-primary placeholder:text-text-muted outline-none focus:border-accent focus:ring-2 focus:ring-accent-muted transition-all"
            />
            <button
              type="submit"
              disabled={!quickAddTitle.trim() || isAdding}
              className="p-2 bg-accent text-surface-base rounded-lg border-none cursor-pointer hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Plus size={18} />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
